import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { AppDispatch, RootState } from '../../../Redux/Store';
import {
  createSeatType,
  deleteSeatType,
  fetchSeatTypes,
  updateSeatType,
} from '../../../Redux/ReservationSettings/reservationSettingsApi';
import { ReservationSeatType } from '../../../types/reservation';
import ConfirmDialog from '../../../Components/Dialog/ConfirmDialog';
import NumberField from '../../../Components/NumberInput/NumberField';
import { SavedSnackbar, TabDescription, useSaveFeedback } from './SettingsShared';

interface Props {
  storeId: string;
}

/** 追加/編集ダイアログのフォーム状態 */
interface SeatTypeForm {
  name: string;
  capacityMin: number;
  capacityMax: number;
  unitCount: number;
  /** 1組で使える最大卓数（席の連結）。1=結合なし */
  maxCombinedUnits: number;
}

const emptyForm: SeatTypeForm = {
  name: '',
  capacityMin: 1,
  capacityMax: 2,
  unitCount: 1,
  maxCombinedUnits: 1,
};

/**
 * 席在庫の統一表記（フィードバックD）: 「最大{capacityMax}名 × {unitCount}卓（連結時{capacityMax×maxCombinedUnits}名）」。
 * maxCombinedUnits<=1 のときは「（連結時…）」を省略する。
 */
export const seatCapacitySummary = (seatType: {
  capacityMax: number;
  unitCount: number;
  maxCombinedUnits?: number;
}): string => {
  const combined = seatType.maxCombinedUnits ?? 1;
  const combinedNote =
    combined > 1 ? `（連結時${seatType.capacityMax * combined}名）` : '';
  return `最大${seatType.capacityMax}名 × ${seatType.unitCount}卓${combinedNote}`;
};

/** 席タイプ1行のラベル（席名 + 統一表記）。 */
const seatCapacityLabel = (seatType: ReservationSeatType): string =>
  `${seatType.name} ${seatCapacitySummary(seatType)}`;

/**
 * タブ②席・在庫設定。席タイプ（名前・収容人数・卓数）の CRUD。
 * capacityMode=SEAT_TYPE のときの空き判定に使われるマスタを管理する。
 */
const SeatTypesTab: React.FC<Props> = ({ storeId }) => {
  const dispatch = useDispatch<AppDispatch>();
  const seatTypes = useSelector((state: RootState) => state.reservationSettings.seatTypes);

  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  // ダイアログ状態。editing=null なら新規追加
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ReservationSeatType | null>(null);
  const [form, setForm] = useState<SeatTypeForm>(emptyForm);
  const [formError, setFormError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ReservationSeatType | null>(null);
  const { savedOpen, closeSaved, showSaved, error, setError, showError } = useSaveFeedback();

  useEffect(() => {
    dispatch(fetchSeatTypes(storeId))
      .unwrap()
      .catch(showError)
      .finally(() => setLoaded(true));
  }, [dispatch, storeId, showError]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setFormError(null);
    setDialogOpen(true);
  };

  const openEdit = (seatType: ReservationSeatType) => {
    setEditing(seatType);
    setForm({
      name: seatType.name,
      capacityMin: seatType.capacityMin,
      capacityMax: seatType.capacityMax,
      unitCount: seatType.unitCount,
      maxCombinedUnits: seatType.maxCombinedUnits ?? 1,
    });
    setFormError(null);
    setDialogOpen(true);
  };

  const submit = () => {
    if (!form.name.trim()) {
      setFormError('名前を入力してください。');
      return;
    }
    if (form.capacityMin < 1 || form.capacityMax < form.capacityMin) {
      setFormError('収容人数は 1 以上で、最小 ≦ 最大 となるように入力してください。');
      return;
    }
    if (form.unitCount < 1) {
      setFormError('卓数は 1 以上を入力してください。');
      return;
    }
    if (form.maxCombinedUnits < 1) {
      setFormError('1組で使える最大卓数は 1 以上を入力してください。');
      return;
    }
    // sortIndex は新規なら末尾（既存最大+1）、編集なら現状維持
    const sortIndex = editing
      ? editing.sortIndex
      : seatTypes.length > 0
        ? Math.max(...seatTypes.map((s) => s.sortIndex)) + 1
        : 0;
    const input = { ...form, name: form.name.trim(), sortIndex };
    setBusy(true);
    const action = editing
      ? dispatch(updateSeatType({ storeId, seatTypeId: editing.id, input }))
      : dispatch(createSeatType({ storeId, input }));
    action
      .unwrap()
      .then(() => {
        setDialogOpen(false);
        showSaved();
      })
      .catch(showError)
      .finally(() => setBusy(false));
  };

  const confirmDelete = () => {
    if (!deleteTarget) return;
    setBusy(true);
    dispatch(deleteSeatType({ storeId, seatTypeId: deleteTarget.id }))
      .unwrap()
      .then(() => showSaved())
      .catch(showError)
      .finally(() => {
        setDeleteTarget(null);
        setBusy(false);
      });
  };

  if (!loaded) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // 合計サマリ（この設定で同時に受け入れられる上限の可視化）
  const totalParties = seatTypes.reduce((sum, st) => sum + st.unitCount, 0);
  const totalPeople = seatTypes.reduce((sum, st) => sum + st.capacityMax * st.unitCount, 0);
  // 席の連結で1組が入れる最大人数（公開フォームの人数上限に反映される値）
  const maxSinglePartyPeople = seatTypes.reduce(
    (max, st) => Math.max(max, st.capacityMax * (st.maxCombinedUnits ?? 1)),
    0,
  );
  // 連結設定のある席タイプが1つでもあるか（サマリ文言の出し分け）
  const hasCombining = seatTypes.some((st) => (st.maxCombinedUnits ?? 1) > 1);

  return (
    <Box>
      <TabDescription text="席の種類と数を登録すると、時間帯ごとの空き判定が席単位で行われます（容量モードが『席タイプで管理』のとき有効）" />

      {seatTypes.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          この設定で同時に最大{totalParties}組・{totalPeople}名まで受け入れられます
          {hasCombining && `（席の連結で1組あたり最大${maxSinglePartyPeople}名まで受付可）`}
        </Alert>
      )}

      {/* 自動割当の考え方を一言で（大人数→大席、少人数→小席） */}
      <Alert severity="info" icon={false} sx={{ mb: 2 }}>
        大人数のグループには大きい席から、少人数には小さい席から自動で割り当てます。
        必要に応じて席を連結し、無駄の少ない席を優先します。
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openCreate}>
          席タイプを追加
        </Button>
      </Box>

      {seatTypes.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
          席タイプはまだ登録されていません。
        </Typography>
      ) : (
        <TableContainer component={Paper} elevation={2}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>席タイプ</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {seatTypes.map((seatType) => (
                <TableRow key={seatType.id}>
                  {/* 「4名席（2〜4名）最大4名（連結時8名）× 2卓」形式で受付上限との関係を可視化する */}
                  <TableCell>{seatCapacityLabel(seatType)}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <IconButton size="small" onClick={() => openEdit(seatType)} aria-label="編集">
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => setDeleteTarget(seatType)}
                      aria-label="削除"
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* 追加/編集ダイアログ */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{editing ? '席タイプを編集' : '席タイプを追加'}</DialogTitle>
        <DialogContent>
          {formError && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {formError}
            </Alert>
          )}
          <TextField
            label="名前（例: 2名席・個室）"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            fullWidth
            margin="dense"
            size="small"
            inputProps={{ maxLength: 50 }}
          />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <NumberField
              label="収容人数（最小）"
              value={form.capacityMin}
              onChangeValue={(capacityMin) => setForm({ ...form, capacityMin })}
              min={0}
              fullWidth
              margin="dense"
              size="small"
            />
            <NumberField
              label="収容人数（最大）"
              value={form.capacityMax}
              onChangeValue={(capacityMax) => setForm({ ...form, capacityMax })}
              min={0}
              fullWidth
              margin="dense"
              size="small"
            />
          </Box>
          <NumberField
            label="卓数（室数）"
            value={form.unitCount}
            onChangeValue={(unitCount) => setForm({ ...form, unitCount })}
            min={0}
            fullWidth
            margin="dense"
            size="small"
          />
          <NumberField
            label="1組で使える最大卓数（席の連結）"
            value={form.maxCombinedUnits}
            onChangeValue={(maxCombinedUnits) => setForm({ ...form, maxCombinedUnits })}
            min={0}
            fullWidth
            margin="dense"
            size="small"
            helperText="複数の席をつなげて大人数を受け入れられます。例: 4名席で「2」にすると、5〜8名のグループを2卓つなげて受け付けます（1=連結なし）"
          />
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setDialogOpen(false)} disabled={busy}>
            キャンセル
          </Button>
          <Button variant="contained" onClick={submit} disabled={busy}>
            {busy ? <CircularProgress size={22} /> : '保存'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 削除確認 */}
      <ConfirmDialog
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        title="席タイプの削除"
        message={`「${deleteTarget?.name ?? ''}」を削除します。よろしいですか？`}
        confirmText="削除する"
        confirmColor="error"
        closeColor="inherit"
      />

      <SavedSnackbar open={savedOpen} onClose={closeSaved} />
    </Box>
  );
};

export default SeatTypesTab;
